// receipts.js — universal receipt parser. One Claude call, normalized output.
//
// Handles every kind of source we accept:
//   - Email body text (Whole Foods, Amazon Fresh, Instacart, etc.)
//   - Email HTML (we strip to text)
//   - Photo of paper receipt (Trader Joe's, etc.) — vision pass
//   - Photo of digital receipt (screenshot from a delivery app)
//
// Returns a normalized object:
// {
//   source: "whole_foods" | "instacart" | "trader_joes" | "unknown",
//   store_name: "Whole Foods Market",
//   order_id: "...",                   // null if absent
//   order_total_cents: 4523,
//   order_date: "2025-11-15",
//   items: [
//     {
//       name: "organic spinach",       // expanded from "ORG SPINACH 5OZ"
//       quantity: 1,
//       unit: "bag" | "lb" | "oz" | "ea" | "bunch",
//       category: "produce",           // produce | pantry | dairy | meat | bakery | frozen | beverage | snack | household | other
//       price_cents: 499,
//       raw_line: "ORG SPINACH 5OZ      $4.99",
//       confidence: "high" | "medium" | "low",
//       skip: false                    // true for tax lines, bag fees, discounts
//     }
//   ],
//   parser_notes: "Anything ambiguous worth flagging to the user"
// }

import { fileToBase64, fileMediaType } from "./supabase.js";
import { callLLM } from "./llm.js";

const RECEIPT_SYSTEM_PROMPT = `You are a precise receipt parser. Extract grocery and food items from a receipt or order email and return strict JSON.

Rules:
1. Output ONLY valid JSON. No prose, no markdown fences, no commentary.
2. Expand abbreviated names: "ORG" → "organic", "SPGHT" → "spaghetti", "AVO" → "avocado", etc. Use the user's most likely intended name.
3. Categorize each item: produce | pantry | dairy | meat | bakery | frozen | beverage | snack | household | other.
4. Mark skip=true for non-grocery lines: tax, bag fees, tip, delivery fees, service fees, discounts that aren't tied to a specific item, donation prompts.
5. For weight items, set unit="lb" or "oz" and quantity to the actual weight if shown. For count items use unit="ea" with quantity as count.
6. confidence: "high" if the line is unambiguous; "medium" if you had to guess at expansion; "low" if the line is hard to read or interpret.
7. If you cannot identify the store, set source="unknown" and store_name to your best guess.
8. order_id: extract if present (look for "Order #", "Order ID", "ORD-", etc.). Null otherwise.
9. order_total_cents: the receipt grand total in cents (e.g., $45.23 → 4523). Null if unparseable.
10. order_date: ISO date (YYYY-MM-DD) of the transaction. Null if unparseable.
11. If the input is clearly NOT a grocery/food receipt (e.g., book purchase, electronics), return {"items":[], "parser_notes":"Not a grocery receipt"}.

Output schema (every field required, use null for absent values):
{
  "source": "whole_foods|amazon_fresh|instacart|trader_joes|costco|kroger|sprouts|unknown",
  "store_name": "string",
  "order_id": "string or null",
  "order_total_cents": number or null,
  "order_date": "YYYY-MM-DD or null",
  "items": [{"name":"...","quantity":1,"unit":"...","category":"...","price_cents":499,"raw_line":"...","confidence":"high|medium|low","skip":false}],
  "parser_notes": "string"
}`;

const COFFEE_SHIPMENT_SYSTEM_PROMPT = `You are a coffee shipment confirmation parser. Extract bag details from a roaster's order confirmation email or photo and return strict JSON.

Rules:
1. Output ONLY valid JSON. No prose, no markdown fences.
2. Identify the roaster (e.g., "Sey Coffee", "Onyx", "Blue Bottle", "Counter Culture", "Bottomless").
3. Extract bag name, origin (country and region if available), roast date if mentioned, weight, tasting notes, price.
4. If multiple bags in one order, return an array of bags.
5. Roast date format: ISO (YYYY-MM-DD). Null if absent.
6. weight_g: convert oz to grams (1 oz = 28.35g). Null if absent.

Output schema:
{
  "roaster": "string",
  "source": "bottomless|trade|atlas|blue_bottle|onyx|counter_culture|sey|generic",
  "order_id": "string or null",
  "bags": [
    {
      "name": "string",
      "origin": ["country", "region"],
      "process": "washed|natural|honey|anaerobic|null",
      "roast_level": "light|medium-light|medium|medium-dark|dark|null",
      "roast_date": "YYYY-MM-DD or null",
      "weight_g": number or null,
      "tasting_notes": ["string"],
      "price_cents": number or null
    }
  ],
  "parser_notes": "string"
}`;

/**
 * Parse a receipt from email text (raw text body of an order email).
 */
export async function parseReceiptFromText({ apiKey, text, subject = "" }) {
  if (!text || !text.trim()) throw new Error("Empty receipt text");

  const userMsg = subject
    ? `Email subject: ${subject}\n\n--- BODY ---\n\n${text}`
    : text;

  return await callClaude({ apiKey, systemPrompt: RECEIPT_SYSTEM_PROMPT, userContent: userMsg });
}

/**
 * Parse a receipt from a photo (e.g., Trader Joe's paper receipt).
 */
export async function parseReceiptFromPhoto({ apiKey, file }) {
  if (!file) throw new Error("No file provided");

  const base64 = await fileToBase64(file);
  const mediaType = fileMediaType(file);

  return await callClaude({
    apiKey,
    systemPrompt: RECEIPT_SYSTEM_PROMPT,
    userContent: [
      {
        type: "image",
        source: { type: "base64", media_type: mediaType, data: base64 },
      },
      {
        type: "text",
        text: "Parse this receipt photo. Return strict JSON only.",
      },
    ],
  });
}

/**
 * Parse a coffee shipment from email text.
 */
export async function parseCoffeeShipmentFromText({ apiKey, text, subject = "" }) {
  if (!text || !text.trim()) throw new Error("Empty shipment text");

  const userMsg = subject
    ? `Email subject: ${subject}\n\n--- BODY ---\n\n${text}`
    : text;

  return await callClaude({ apiKey, systemPrompt: COFFEE_SHIPMENT_SYSTEM_PROMPT, userContent: userMsg });
}

/**
 * Parse a coffee shipment from a photo (e.g., bag label).
 */
export async function parseCoffeeShipmentFromPhoto({ apiKey, file }) {
  const base64 = await fileToBase64(file);
  const mediaType = fileMediaType(file);

  return await callClaude({
    apiKey,
    systemPrompt: COFFEE_SHIPMENT_SYSTEM_PROMPT,
    userContent: [
      { type: "image", source: { type: "base64", media_type: mediaType, data: base64 } },
      { type: "text", text: "Parse this coffee shipment / bag label. Return strict JSON only." },
    ],
  });
}

// ----- shared Claude call -----

async function callClaude({ apiKey, systemPrompt, userContent, feature = "Receipt parsing" }) {
  // Callers pass either a plain string or an array of Anthropic content blocks
  // (photo flows include image blocks). Split those apart so each provider's
  // adapter can render images in its own format.
  let userText = "";
  const images = [];
  if (typeof userContent === "string") {
    userText = userContent;
  } else {
    for (const block of userContent || []) {
      if (block.type === "text") {
        userText += (userText ? "\n" : "") + block.text;
      } else if (block.type === "image" && block.source?.type === "base64") {
        images.push({ mediaType: block.source.media_type, data: block.source.data });
      }
    }
  }

  const { text } = await callLLM({
    apiKey,
    system: systemPrompt,
    messages: [{ role: "user", content: userText }],
    images: images.length ? images : undefined,
    maxTokens: 4096,
    kind: "receipts",
    feature,
  });

  // Strip markdown fences if model accidentally added them
  const cleaned = text
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/```\s*$/i, "")
    .trim();

  try {
    return JSON.parse(cleaned);
  } catch (e) {
    throw new Error(`Couldn't parse Claude response as JSON. First 200 chars: ${cleaned.slice(0, 200)}`);
  }
}

// ----- pantry SKU matching -----

/**
 * For each parsed item, try to match against the user's existing pantry SKUs.
 * Returns { matched: [...], new: [...] } so the UI can show "increment 3 cans"
 * vs "create new SKU."
 *
 * Match on normalized name; fall back to fuzzy substring.
 */
export function matchItemsToSkus(parsedItems, existingSkus) {
  const matched = [];
  const fresh = [];
  const skuByNormalizedName = new Map(
    (existingSkus || []).map(s => [normalize(s.canonical_name || s.name || ""), s])
  );

  for (const item of parsedItems || []) {
    if (item.skip) continue;
    const norm = normalize(item.name);
    let sku = skuByNormalizedName.get(norm);

    // Fuzzy: any SKU that contains all words of the parsed name
    if (!sku) {
      const words = norm.split(/\s+/).filter(Boolean);
      for (const [key, candidate] of skuByNormalizedName.entries()) {
        if (words.length && words.every(w => key.includes(w))) {
          sku = candidate;
          break;
        }
      }
    }

    if (sku) {
      matched.push({ item, sku });
    } else {
      fresh.push(item);
    }
  }

  return { matched, new: fresh };
}

function normalize(s) {
  return String(s || "")
    .toLowerCase()
    .replace(/[^a-z0-9 ]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

// ----- store hint detection (cheap, no AI) -----

/**
 * Quick heuristic to detect store from email subject + sender + body before
 * making an AI call. Useful for routing and for prepopulating the source field.
 */
export function detectStoreHint({ subject = "", from = "", body = "" }) {
  const text = `${subject} ${from} ${body.slice(0, 500)}`.toLowerCase();
  if (text.includes("whole foods") || text.includes("wholefoodsmarket.com")) return "whole_foods";
  if (text.includes("amazon fresh") || text.includes("amzn.com/fresh")) return "amazon_fresh";
  if (text.includes("instacart")) return "instacart";
  if (text.includes("trader joe")) return "trader_joes";
  if (text.includes("costco")) return "costco";
  if (text.includes("kroger")) return "kroger";
  if (text.includes("sprouts")) return "sprouts";
  if (text.includes("safeway")) return "safeway";
  return "unknown";
}

export function detectCoffeeRoasterHint({ subject = "", from = "", body = "" }) {
  const text = `${subject} ${from} ${body.slice(0, 500)}`.toLowerCase();
  if (text.includes("bottomless")) return "bottomless";
  if (text.includes("trade coffee") || text.includes("drinktrade")) return "trade";
  if (text.includes("atlas coffee")) return "atlas";
  if (text.includes("blue bottle")) return "blue_bottle";
  if (text.includes("onyx coffee")) return "onyx";
  if (text.includes("counter culture")) return "counter_culture";
  if (text.includes("sey coffee")) return "sey";
  return "generic";
}
